/**
 * ZoneSheetPrintPage — printable Five Below Daily Zone Sheet + Daily
 * Manager Checklist. Rebuilt 2026-06-12 against the user's actual
 * in-store PDF (Zone+Sheet+and+Manager+Checklist-2.pdf):
 *
 *   - Strict 2-page output. Page 1 Zone Sheet. Page 2 Manager
 *     Checklist. No page 3 overflow.
 *   - Five Below Gothic loaded inline via an absolute URL so the
 *     print context (a fresh tab popup) doesn't lose the font.
 *   - Grayscale palette matching the source PDF — section bars are
 *     light gray, completion cells stay plain. No app-shell color
 *     accents (blue/yellow) that don't exist on the in-store form.
 *   - Manager signoffs render the SIGNER's name (Opening MOD = DOM,
 *     etc.) by merging crew roster into the name lookup, so the
 *     Complete? column always populates with whoever ticked the box
 *     in the live app — not a generic "Manager" placeholder.
 *
 * Renders from `?route=zone-sheet-print&date=YYYY-MM-DD&store_id=...&actor_id=...`.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { getCheckpointLabels } from "../lib/checkpointLabels";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FB_LOGO = "https://customer-assets.emergentagent.com/job_inbox-pulse-4/artifacts/r9roxonr_Image%201.jpeg";
// Absolute URL so the popup can resolve the font even if its <base href>
// differs from the React shell. `PUBLIC_URL` is set by CRA at build time;
// falls back to "" so dev still works.
const FB_FONT_URL = `${process.env.PUBLIC_URL || ""}/fonts/FiveBelowGothic-Regular.ttf`;

const fmtPretty = (ymd) => {
  if (!ymd) return "";
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
};
const fmtMoney = (n) => (n == null || n === "" || Number.isNaN(Number(n)) ? "" : `$${Number(n).toLocaleString()}`);
const fmtNum = (n) => (n == null || n === "" || Number.isNaN(Number(n)) ? "" : Number(n).toLocaleString());

export default function ZoneSheetPrintPage({ embedDate, embedStoreId, onClose } = {}) {
  // Embed mode (2026-06-17): when rendered inline as an overlay from
  // ZoneSheetPage (props are passed), we read the date/store from
  // props instead of URL params. This is the default path for the
  // "Print" button now because iOS PWAs open `window.open(..., '_blank')`
  // in external Safari, which has a SEPARATE localStorage from the
  // PWA — so the URL/new-tab flow was booting authenticated managers
  // back to login. Inline rendering shares the parent tab's auth
  // context, eliminating that whole class of bug.
  //
  // Standalone URL mode (legacy / deep-link) still works for direct
  // visits to `/?route=zone-sheet-print&date=…` — the AppRouter wrapper
  // in App.js still mounts this component when the URL matches, and
  // the auth gate below still redirects unauthenticated visitors.
  const isEmbedded = !!embedDate;
  const params = new URLSearchParams(window.location.search);
  const date = embedDate || params.get("date") || new Date().toLocaleDateString("en-CA");
  const lsUser = (() => { try { return JSON.parse(localStorage.getItem("ecDashUser") || "{}"); } catch { return {}; } })();
  // SECURITY: `actor_id` is read ONLY from the authenticated session
  // (localStorage.ecDashUser.crew_id). The URL `?actor_id=…` query
  // param is intentionally NOT consulted — it was previously acting
  // as a permanent bearer token that any leaked print URL could
  // exploit. (P0 fix 2026-06-16; gate enforced one layer up in
  // App.js's AppRouter so unauthenticated users never reach this
  // component.) `store_id` falls back to URL → localStorage so the
  // standalone URL flow works; embed mode just passes the active
  // store directly via props (handles admin/DM "view as store"
  // mirroring at the parent without depending on URL state).
  const actorId = lsUser?.crew_id || "";
  const storeId = embedStoreId || params.get("store_id") || lsUser?.home_store_id || "";

  const [sheet, setSheet] = useState(null);
  const [biz, setBiz] = useState(null);
  const [wtdRollup, setWtdRollup] = useState(null);
  const [tally, setTally] = useState(null);
  const [store, setStore] = useState(null);
  const [crewById, setCrewById] = useState({});
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState("");

  // ── Store Focus auto-fit ───────────────────────────────────────
  // The "Store Focus Areas" box is a FIXED-height cell (so a long
  // focus can't push the sheet onto a 3rd page). A long message used
  // to simply CLIP at the bottom (user 2026-06: "it's cutting it
  // off"). Instead of truncating, we shrink the font just enough that
  // the whole message fits the box. The catch: the box is ~185px wide
  // when PRINTED on Letter but much wider in the on-screen preview, so
  // we can't measure the live element. We measure against a hidden
  // "ruler" locked to the printed column width, pick the largest font
  // whose wrapped height fits the box, and apply it inline so it holds
  // in the generated PDF too.
  const focusRef = useRef(null);
  const rulerRef = useRef(null);
  useLayoutEffect(() => {
    const box = focusRef.current;
    if (!box) return undefined;
    const fit = () => {
      const text = box.textContent || "";
      let ruler = rulerRef.current;
      if (!ruler) {
        ruler = document.createElement("div");
        ruler.setAttribute("aria-hidden", "true");
        rulerRef.current = ruler;
        document.body.appendChild(ruler);
      }
      const cs = window.getComputedStyle(box);
      ruler.style.cssText = [
        "position:absolute", "left:-99999px", "top:0", "visibility:hidden",
        "box-sizing:border-box",
        "width:185px",            // 1fr of the Letter 2fr/1fr/1fr grid
        "padding:5px",            // matches .zs-page-1 .zs-focus-area
        "line-height:1.25",
        "white-space:pre-wrap",
        "word-break:break-word",
        `font-family:${cs.fontFamily}`,
      ].join(";");
      ruler.textContent = text;
      const MAX = 11, MIN = 6, LIMIT = 140; // printed box max-height (px)
      let size = MAX;
      ruler.style.fontSize = size + "px";
      let guard = 0;
      while (size > MIN && ruler.offsetHeight > LIMIT && guard < 80) {
        size -= 0.25;
        ruler.style.fontSize = size + "px";
        guard += 1;
      }
      box.style.display = "block";
      box.style.webkitLineClamp = "unset";
      box.style.lineHeight = "1.25";
      box.style.fontSize = size + "px";
    };
    const raf = requestAnimationFrame(fit);
    return () => {
      cancelAnimationFrame(raf);
      const r = rulerRef.current;
      if (r && r.parentNode) r.parentNode.removeChild(r);
      rulerRef.current = null;
    };
  }, [sheet, settings]);

  // Fail-closed redirect: if no authenticated session is present in
  // localStorage (post-deploy `_setItem('ecDashUser', ...)`), bounce
  // the standalone URL visitor to login and stash the print URL in
  // `localStorage.postLoginRedirect` so LoginPage can return them
  // here after auth. Skipped in embed mode because the parent page
  // already enforces auth — there's no way to land in an embed
  // without going through the authenticated app shell first.
  useEffect(() => {
    if (isEmbedded) return;
    if (!actorId) {
      try {
        localStorage.setItem("postLoginRedirect", window.location.pathname + window.location.search);
        sessionStorage.setItem("ecDashLogoutReason", "auth_required_for_zone_sheet_print");
        window.location.replace("/");
      } catch { /* ignore */ }
    }
  }, [actorId, isEmbedded]);

  useEffect(() => {
    if (!actorId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const [s, b, w, t, c, st] = await Promise.allSettled([
          axios.get(`${API}/zone-sheets/${date}`, { params: { store_id: storeId, actor_id: actorId } }),
          axios.get(`${API}/biz/${date}`, { params: { store_id: storeId } }),
          // WTD rollup — same endpoint the Manager Dashboard WTD
          // Financials tile uses. Previously the print page tried to
          // read `biz?.wtd` (never populated by `/biz/{date}`) which
          // left every WTD cell blank on the printed PDF. (User bug
          // report 2026-06-13.)
          axios.get(`${API}/biz/wtd/${date}`, { params: { store_id: storeId } }),
          axios.get(`${API}/tally/${date}`, { params: { store_id: storeId } }),
          axios.get(`${API}/crew`, { params: { store_id: storeId } }),
          // Settings (with store-id scoped hours override) — feeds the
          // checkpoint label generator so the printed PDF shows the
          // store's actual checkpoint times (e.g. 11AM / 4PM / 9:30PM
          // for Colorado Mills mall hours), not the chain defaults.
          // (Phase C 2026-06-16.)
          axios.get(`${API}/settings`, { params: { store_id: storeId } }),
        ]);
        if (cancelled) return;
        if (s.status === "fulfilled") setSheet(s.value.data);
        else setError(s.reason?.response?.data?.detail || "Couldn't load Zone Sheet");
        if (b.status === "fulfilled") setBiz(b.value.data);
        if (w.status === "fulfilled") setWtdRollup(w.value.data);
        if (t.status === "fulfilled") setTally(t.value.data);
        if (c.status === "fulfilled") {
          // Build id → first_name map so manager sign-offs resolve to
          // the real signer (DOM, WENDY, etc.) instead of falling back
          // to a generic "Manager" placeholder.
          const map = {};
          (c.value.data || []).forEach((m) => {
            if (m.id) map[m.id] = (m.first_name || m.name || "").toUpperCase();
          });
          setCrewById(map);
        }
        if (st.status === "fulfilled") setSettings(st.value.data);
        try {
          const me = await axios.get(`${API}/auth/me`, { params: { actor_id: actorId } });
          setStore(me.data?.store || null);
        } catch { /* ignore */ }
      } catch (e) {
        if (!cancelled) setError(String(e?.message || e));
      }
    })();
    return () => { cancelled = true; };
  }, [date, storeId, actorId]);

  if (!actorId && !isEmbedded) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>Redirecting to login…</div>;
  if (error) return <ErrorView msg={error} />;
  if (!sheet) return <div style={{ padding: 40, fontFamily: "sans-serif" }}>Loading print preview…</div>;

  const cps = biz?.checkpoints || {};
  const cp2 = cps["2pm"] || {}, cp5 = cps["5pm"] || {}, cp8 = cps["8pm"] || {};
  // Pull WTD from the same `/api/biz/wtd/{date}` endpoint the Manager
  // Dashboard WTD Financials tile uses, so the printed PDF and the
  // dashboard never disagree. Field names match the backend rollup
  // (`actual_sales`, `plan_sales`, `diff_plan_dollars`, etc.) — see
  // `_compute_wtd_rollup` in server.py.
  const wtd = wtdRollup || {};
  // Friendly +/- formatter: positive → "$1,234", negative → "($1,234)".
  // Treats null/missing/0 as empty so the PDF doesn't show "$0" cells
  // for a fresh week where the manager hasn't entered Plan / LY yet.
  // Rounded to whole dollars to match the Manager Dashboard convention.
  const fmtMoneySigned = (n) => {
    if (n == null || n === "" || Number.isNaN(Number(n))) return "";
    const v = Math.round(Number(n));
    if (v === 0) return "$0";
    return v < 0 ? `($${Math.abs(v).toLocaleString()})` : `$${v.toLocaleString()}`;
  };
  const fmtPct = (n) => {
    if (n == null || n === "" || Number.isNaN(Number(n))) return "";
    const pct = Number(n) * 100;
    return `${pct >= 0 ? "" : "-"}${Math.abs(pct).toFixed(0)}%`;
  };
  const fmtRatio = (n) => (n == null || n === "" || Number.isNaN(Number(n)) ? "" : Number(n).toLocaleString());

  // Name lookup that merges the full crew roster with any names cached
  // on crew_assignments. Sign-offs by managers who aren't actively on
  // today's coverage table still resolve to their first name.
  const nameById = { ...crewById };
  (sheet.crew_assignments || []).forEach((r) => {
    if (r.crew_id && r.crew_name) nameById[r.crew_id] = String(r.crew_name).toUpperCase();
  });
  const nm = (id) => (id ? (nameById[id] || "MANAGER") : "");

  return (
    <div className="zs">
      <PrintStyles />
      <div className="zs-toolbar">
        <button
          onClick={() => {
            // Embed mode: parent page handles unmount via onClose. Standalone
            // mode (cold URL visit): close the tab the user came from.
            if (isEmbedded && typeof onClose === "function") onClose();
            else window.close();
          }}
          data-testid="zs-print-close"
        >Close</button>
        <button className="primary" onClick={() => window.print()} data-testid="zs-print-go">Print</button>
      </div>

      {/* ─────────── PAGE 1 — DAILY ZONE SHEET ─────────── */}
      <div className="zs-page zs-page-1">
        <header className="zs-head">
          <div className="zs-title">
            <h1>DAILY ZONE SHEET</h1>
            <div className="zs-date"><strong>TODAY&apos;S DATE:</strong> <span className="zs-fill">{fmtPretty(date)}{store?.code ? ` · Store ${store.code}` : ""}</span></div>
          </div>
          <div className="zs-howto">
            <div className="zs-howto-lbl">ZONE SHEET HOW-TO:</div>
            <div className="zs-qr-small"><QRCodeSVG value="https://wowcrewapp.com/zone-sheet-how-to" size={46} level="M" /></div>
          </div>
          <img src={FB_LOGO} alt="five BELOW" className="zs-logo" onError={(e) => { if (e.currentTarget.src.indexOf("/fivebelow-logo.png") === -1) e.currentTarget.src = "/fivebelow-logo.png"; }} />
        </header>

        <h2 className="zs-h2">EYES ON THE BIZ</h2>
        {/* Page 1 retains the full Today + Week-to-Date layout from the
            original in-store form. The LIVE app view hides WTD
            (relocated to Manager Dashboard for confidentiality), but the
            PRINTED PDF keeps it so the physical sheet matches what
            managers are used to filling in. (User clarification
            2026-06-12 — "WTD should stay on the printed PDF because the
            PDF is supposed to match the original in-store Zone Sheet
            layout".) */}
        <div className="zs-eob-split">
          {/* TODAY — checkpoint readings */}
          <table className="zs-eob">
            <thead>
              <tr><th colSpan={4} className="zs-grp">TODAY</th></tr>
              <tr>
                <th className="zs-sub">Plan $</th>
                <td className="zs-fb">{fmtMoney(biz?.plan_sales)}</td>
                <th className="zs-sub">Comp Goal $</th>
                <td className="zs-fb">{fmtMoney(biz?.comp_goal_sales)}</td>
              </tr>
              <tr>
                <th></th>
                <th>READS</th>
                <th>SALES</th>
                <th>BOPIS ORDERS</th>
              </tr>
            </thead>
            <tbody>
              {/* BOPIS field on each checkpoint is persisted as
                  `bopis` (matches the backend `cells` shape in
                  server.py + the Manager Dashboard `kpi.bopis` read).
                  The print page was incorrectly reading
                  `bopis_orders`, which is never populated — leaving
                  every BOPIS cell blank on the printed PDF. (Bug
                  fix 2026-06-15.)
                  Checkpoint row labels (2pm / 5pm / 8pm) come from
                  `getCheckpointLabels(settings, date)` so a store
                  with overridden hours (e.g. mall hours 11AM/4PM/
                  9:30PM) shows the right times on the printed sheet
                  too. (Phase C 2026-06-16.) */}
              {(() => {
                const cpLbl = getCheckpointLabels(settings || {}, date);
                return (
                  <>
                    <tr><th className="zs-eob-row">{cpLbl["2pm"]}</th><td>{fmtNum(cp2?.reads)}</td><td>{fmtMoney(cp2?.sales)}</td><td>{fmtNum(cp2?.bopis)}</td></tr>
                    <tr><th className="zs-eob-row">{cpLbl["5pm"]}</th><td>{fmtNum(cp5?.reads)}</td><td>{fmtMoney(cp5?.sales)}</td><td>{fmtNum(cp5?.bopis)}</td></tr>
                    <tr><th className="zs-eob-row">{cpLbl["8pm"]}</th><td>{fmtNum(cp8?.reads)}</td><td>{fmtMoney(cp8?.sales)}</td><td>{fmtNum(cp8?.bopis)}</td></tr>
                  </>
                );
              })()}
            </tbody>
          </table>

          {/* WEEK TO DATE + KPIs — restored 2026-06-12. Data wired
              2026-06-13 to `/api/biz/wtd/{date}` (same source as
              Manager Dashboard WTD Financials tile) so the printed
              PDF and the dashboard always agree. Previously the
              cells were empty because the page tried to read a
              non-existent `biz.wtd` field. */}
          <table className="zs-eob">
            <thead>
              <tr><th colSpan={7} className="zs-grp">WEEK TO DATE</th></tr>
              <tr>
                <th colSpan={2} className="zs-sub">WTD SALES PLAN</th>
                <th colSpan={2} className="zs-sub">WTD COMP SALES</th>
                <th colSpan={3} className="zs-sub">KPIS</th>
              </tr>
              <tr>
                <th className="zs-lbl">Actual $</th><td className="zs-fb">{fmtMoney(wtd?.actual_sales)}</td>
                <th className="zs-lbl">Actual $</th><td className="zs-fb">{fmtMoney(wtd?.actual_comp_sales)}</td>
                <th className="zs-lbl">ADS</th><td className="zs-fb">{fmtMoney(wtd?.ads)}</td>
                <th className="zs-lbl zs-narrow">%to LY</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th className="zs-lbl">Plan $</th><td className="zs-fb">{fmtMoney(wtd?.plan_sales)}</td>
                <th className="zs-lbl">LY Sales $</th><td className="zs-fb">{fmtMoney(wtd?.ly_sales)}</td>
                <th className="zs-lbl">UPT</th><td className="zs-fb">{fmtRatio(wtd?.upt)}</td>
                <th className="zs-lbl zs-narrow">%to LY</th>
              </tr>
              <tr>
                <th className="zs-lbl">vs. +/- PLAN $</th><td className="zs-fb">{fmtMoneySigned(wtd?.diff_plan_dollars)}</td>
                <th className="zs-lbl">vs. +/- LY COMP $</th><td className="zs-fb">{fmtMoneySigned(wtd?.diff_ly_dollars)}</td>
                <th className="zs-lbl">Fill Rate</th><td className="zs-fb">{wtd?.fill_rate != null ? `${fmtRatio(wtd.fill_rate)}%` : ""}</td>
                <th className="zs-lbl zs-narrow">LW</th>
              </tr>
              <tr>
                <th className="zs-lbl">vs. +/- PLAN %</th><td className="zs-fb">{fmtPct(wtd?.diff_plan_pct)}</td>
                <th className="zs-lbl">vs. +/- LY COMP %</th><td className="zs-fb">{fmtPct(wtd?.diff_ly_pct)}</td>
                <th className="zs-lbl">Email Capture</th><td className="zs-fb">{fmtNum(wtd?.emails_total)}</td>
                <th className="zs-lbl zs-narrow">LW · <span className="zs-fb">{fmtNum(wtd?.emails_last_week)}</span></th>
              </tr>
            </tbody>
          </table>
        </div>

        {/* WORLD RECOVERY · STORE FOCUS · WOWCREW OBSERVATION QR */}
        <div className="zs-row3">
          <div className="zs-box zs-wr-box">
            <div className="zs-box-title">WORLD RECOVERY</div>
            <div className="zs-focus-lbl"><strong>TODAY&apos;S FOCUS:</strong> <span className="zs-fill">{(sheet.world_recovery_focus?.title || "").replace(/^Recovery World\s*[—–-]\s*/i, "").replace(/^Recovery\s*[—–-]\s*/i, "")}</span></div>
            <table className="zs-wr">
              <thead>
                <tr><th></th><th>ASSIGNED TO:</th><th>COMPLETE?</th></tr>
              </thead>
              <tbody>
                {(sheet.zone_tasks || []).map((t) => (
                  <tr key={t.id}>
                    <td className="zs-wr-lbl">{t.label}</td>
                    <td>{t.assigned_to_name || ""}</td>
                    <td className="zs-cmpl">{t.completed ? `✓ ${nm(t.completed_by) || ""}`.trim() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="zs-box">
            <div className="zs-box-title">STORE FOCUS AREAS</div>
            <div className="zs-focus-area" ref={focusRef}>{sheet.store_focus || ""}</div>
          </div>
          <div className="zs-box zs-qr-box">
            <div className="zs-box-title">WOWCREW OBSERVATION QR CODE</div>
            <div className="zs-qr"><QRCodeSVG value="https://wowcrewapp.com/wowcrew-observation" size={84} level="M" /></div>
          </div>
        </div>

        {/* WIN THE DAY! */}
        <h2 className="zs-h2">WIN THE DAY!</h2>
        <WinTheDayGrid items={sheet.win_the_day || []} nm={nm} />

        {/* WOWCREW table */}
        <h2 className="zs-h2">WOWCREW</h2>
        <table className="zs-crew">
          <thead>
            <tr>
              <th>NAME</th><th>SHIFT</th><th>BREAK</th>
              <th>COVERAGE/ZONE/TASKS</th><th>WALKIE</th><th>ZEBRA</th>
            </tr>
          </thead>
          <tbody>
            <tr className="zs-crew-example">
              <td></td><td></td><td></td>
              <td><em>CASHIER (Review Gift Card Policy Daily)</em></td>
              <td></td><td></td>
            </tr>
            {(sheet.crew_assignments || []).map((r) => {
              // 2026-08-30 (v3) — Coverage cell holds ONLY the manager's
              // typed coverage. WTD assignments live in the digital
              // "Your Tasks Today" card + push (not the print sheet)
              // per user 2026-08-30: "the wow crew tasks is an overkill
              // of text". Name column is CENTERED and slightly larger
              // than shift/break so the crew stands out on the printed
              // sheet — WowCrew is celebrated, not squeezed into a
              // corner (user 2026-08-30: "this is wow crew were excited
              // to see them on the zone sheet").
              return (
                <tr key={r.crew_id}>
                  <td className="zs-crew-name">
                    <div>{r.crew_name}</div>
                    {r.is_visitor && r.home_store_code && (
                      <div style={{ color: "#71717a", fontWeight: 600, fontSize: "0.85em", lineHeight: 1.1, marginTop: 1 }}>
                        Visiting {r.home_store_code}
                      </div>
                    )}
                  </td>
                  <td>{r.shift || ""}</td><td>{r.break_time || ""}</td>
                  <td>{r.coverage || ""}</td>
                  <td className="zs-cmpl">{r.walkie ? "✓" : ""}</td>
                  <td className="zs-cmpl">{r.zebra ? "✓" : ""}</td>
                </tr>
              );
            })}
            {/* Pad to a fixed row count so the table footprint is
                consistent. Original PDF reserves ~6 blank rows; bumped
                to 10 (2026-06-18) so the WowCrew table physically
                fills the remaining whitespace on Letter Page 1 even
                when the user's print dialog applies wider default
                margins than our @page rule.
                Dropped to 8 (2026-08-27, user ask: "We have 2 boxes
                that are blank that don't need to be there"). Sheet
                still fills Page 1 for the common 5-7 crew day; two
                fewer empty rows so it doesn't look padded. */}
            {Array.from({ length: Math.max(0, 8 - (sheet.crew_assignments || []).length) }).map((_, i) => (
              <tr key={`b${i}`}><td></td><td></td><td></td><td></td><td></td><td></td></tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ─────────── PAGE 2 — DAILY MANAGER CHECKLIST ─────────── */}
      <div className="zs-page zs-page-2">
        <header className="zs-head">
          <div className="zs-title">
            <h1>DAILY MANAGER CHECKLIST</h1>
            <div className="zs-date"><strong>TODAY&apos;S DATE:</strong> <span className="zs-fill">{fmtPretty(date)}</span></div>
          </div>
          <img src={FB_LOGO} alt="five BELOW" className="zs-logo" onError={(e) => { if (e.currentTarget.src.indexOf("/fivebelow-logo.png") === -1) e.currentTarget.src = "/fivebelow-logo.png"; }} />
        </header>

        <h2 className="zs-h2-sub">OPENING MANAGER TASKS</h2>
        <ChecklistTable items={sheet.opening_checklist || []} nm={nm} />

        <p className="zs-note">CHECK EMAIL + CONNECTS BEFORE END OF SHIFT</p>
        <p className="zs-note">MEET WITH CLOSING MANAGER TO DISCUSS ANY OUTSTANDING TASKS FROM THE DAY AND CREATE A GAMEPLAN FOR COMPLETION</p>

        <h2 className="zs-h2-sub">CLOSING MANAGER TASKS</h2>
        <ChecklistTable items={sheet.closing_checklist || []} nm={nm} />

        <div className="zs-notes-block">
          <div className="zs-notes-lbl">NOTES:</div>
          <div className="zs-notes-fill">{sheet.manager_notes || ""}</div>
        </div>
      </div>
    </div>
  );
}

function ErrorView({ msg }) {
  return (
    <div style={{ padding: 40, fontFamily: "sans-serif" }}>
      <h2>Couldn&apos;t load print sheet</h2>
      <p style={{ color: "#888" }}>{msg}</p>
      <button onClick={() => window.close()}>Close</button>
    </div>
  );
}

function WinTheDayGrid({ items, nm }) {
  const headers = ["FRONT END/SERVICES", "STORE SETUP", "FILLING", "FILLING"];
  // 2026-08-30 (v2) — Single-table layout restored. The previous flex
  // of 4 side-by-side mini-tables broke shared row alignment because
  // each mini-table sized its own rows independently (Tetris look —
  // user rejected). Back to ONE 4-column table so every row height
  // is synced. To eliminate the trailing empty cells the user asked
  // about ("2 rows under PM Recover"), we balance items across cols
  // as evenly as possible (18 items → 5,5,4,4) and tag any leftover
  // trailing empty cell with `zs-wtd-empty` so print CSS renders it
  // as invisible whitespace (no border, no background) — the shorter
  // column just fades out at the bottom instead of showing empty
  // boxes.
  const n = items.length || 1;
  const base = Math.floor(n / 4);
  const extra = n % 4;
  const colSizes = [0, 1, 2, 3].map((i) => base + (i < extra ? 1 : 0));
  const per = Math.max(1, ...colSizes);
  let cursor = 0;
  const cols = colSizes.map((sz) => {
    const slice = items.slice(cursor, cursor + sz);
    cursor += sz;
    return slice;
  });
  return (
    <table className="zs-wtd">
      <colgroup>
        {[0, 1, 2, 3].flatMap((i) => [
          <col key={`lc-${i}`} className="zs-wtd-lbl-col" />,
          <col key={`cc-${i}`} className="zs-wtd-cmpl-col" />,
        ])}
      </colgroup>
      <thead>
        <tr>
          {headers.map((h, i) => (
            <React.Fragment key={`hdr-${i}`}>
              <th className="zs-wtd-h">{h}</th>
              <th className="zs-wtd-h">COMPLETE?</th>
            </React.Fragment>
          ))}
        </tr>
      </thead>
      <tbody>
        {Array.from({ length: per }).map((_, rowIdx) => (
          <tr key={rowIdx}>
            {cols.map((col, ci) => {
              const t = col[rowIdx];
              const signed = !!t?.signed_off_by;
              // Assignee pre-print (2026-08-27): show → <name> for
              // delegated-but-not-signed rows so the print sheet
              // reads at a glance who owns each task.
              let completeCell = "";
              if (signed) {
                completeCell = `✓ ${nm(t.signed_off_by) || ""}`.trim();
              } else if (t?.assigned_to) {
                completeCell = `→ ${t.assigned_to_name || nm(t.assigned_to) || ""}`.trim();
              }
              // Trailing empty cell in a short column — tag it so
              // print CSS strips its borders (invisible whitespace,
              // not a visible empty box).
              const emptyCls = t ? "" : " zs-wtd-empty";
              return (
                <React.Fragment key={`${ci}-${rowIdx}`}>
                  <td className={`zs-wtd-lbl-cell${emptyCls}`}>
                    <div className="zs-wtd-lbl">{t?.label || ""}</div>
                  </td>
                  <td className={`zs-cmpl${emptyCls}`}>{completeCell}</td>
                </React.Fragment>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ChecklistTable({ items, nm }) {
  return (
    <table className="zs-chk">
      <thead><tr><th></th><th>Completed?</th></tr></thead>
      <tbody>
        {(items || []).map((t) => (
          t.type === "header"
            ? <tr key={t.id} className="zs-chk-grp"><td colSpan={2}>{t.label}</td></tr>
            : <tr key={t.id}>
                <td>
                  {t.label}
                  {t.assignable && (
                    <span className="zs-chk-sub"> &nbsp;&nbsp;WowCrew Member: {t.assigned_to_name || "________"}</span>
                  )}
                </td>
                <td className="zs-cmpl">
                  {t.signed_off_by ? (
                    // Admin override audit trail — print "DOM (for JOSUE)"
                    // when the admin signed for someone else, so the
                    // physical sheet reflects who actually completed
                    // it AND who was supposed to. (User spec 2026-06-12
                    // "Completed by: Dom · Override for: Josue".)
                    t.signed_off_override_for
                      ? `✓ ${nm(t.signed_off_by)} (for ${nm(t.signed_off_override_for) || "Mgr"})`
                      : `✓ ${nm(t.signed_off_by)}`.trim()
                  ) : ""}
                </td>
              </tr>
        ))}
      </tbody>
    </table>
  );
}

function PrintStyles() {
  // Inline @font-face uses an absolute URL to /fonts/FiveBelowGothic-Regular.ttf
  // (CRA copies /public verbatim to the build root) so the popup window
  // gets the brand font even when CRA's webpack-fingerprinted asset URL
  // in index.css is invalid in a fresh tab context.
  return (
    <style>{`
      @font-face {
        font-family: 'Five Below Gothic';
        src: url('${FB_FONT_URL}') format('truetype');
        font-weight: 400 900;
        font-style: normal;
        font-display: block;
      }
      @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700;900&display=swap');

      /* ============================================================
         Layout — strict 2-page Letter output (8.5 × 11). Margins kept
         tight so the in-store form's compact grid actually fits without
         spilling into a 3rd page. ============================================ */
      .zs {
        font-family: 'Five Below Gothic', 'Roboto', 'Helvetica Neue', Arial, sans-serif;
        color:#000; background:#fff; padding:10px; max-width:8.5in;
        margin:0 auto; font-size:9px; line-height:1.15;
      }
      .zs-toolbar {
        position:sticky; top:0; background:#f1f5f9; border-bottom:1px solid #cbd5e1;
        padding:6px 10px; display:flex; gap:8px; justify-content:flex-end;
        margin:-10px -10px 8px -10px; z-index:10;
      }
      .zs-toolbar button { padding:5px 12px; font-size:12px; font-weight:700; border-radius:5px; border:1px solid #94a3b8; background:#fff; cursor:pointer; }
      .zs-toolbar button.primary { background:#000; color:#fff; border-color:#000; }

      .zs-page {
        page-break-after:always;
        break-after:page;
      }
      .zs-page:last-child { page-break-after:auto; break-after:auto; }
      .zs-page > *:first-child { margin-top:0 !important; }

      /* ============================================================
         Header — DAILY ZONE SHEET / DAILY MANAGER CHECKLIST. Title in
         Five Below Gothic for the brand voice. Date sits below.
         ============================================================ */
      .zs-head {
        display:grid; grid-template-columns: 1fr auto auto; gap:10px;
        align-items:center; padding-bottom:3px; margin-bottom:4px;
        border-bottom:2px solid #000;
      }
      .zs-title h1 {
        font-family:'Five Below Gothic', 'Roboto', Arial, sans-serif;
        font-size:22px; font-weight:900; margin:0; letter-spacing:.01em;
        text-transform:uppercase; color:#000;
      }
      .zs-date { font-size:9px; margin-top:1px; }
      .zs-fill { border-bottom:1px solid #000; padding:0 4px; display:inline-block; min-width:180px; }
      .zs-howto { text-align:center; font-size:7px; font-weight:700; text-transform:uppercase; }
      .zs-howto-lbl { margin-bottom:1px; letter-spacing:.04em; }
      .zs-qr-small svg { display:block; }
      .zs-logo { height:38px; width:auto; }

      /* ============================================================
         Section bars — grayscale to match the in-store form. The blue
         we previously used was an app-shell carryover that didn't
         match the actual printed sheet. ============================ */
      .zs-h2 {
        font-family:'Five Below Gothic', Arial, sans-serif;
        font-size:11px; font-weight:900; letter-spacing:.04em;
        text-transform:uppercase; margin:4px 0 2px; padding:2px 6px;
        background:#d4d4d8; color:#000; border:1px solid #000;
      }
      .zs-h2-sub {
        font-family:'Five Below Gothic', Arial, sans-serif;
        font-size:11px; font-weight:900; letter-spacing:.04em;
        text-transform:uppercase; margin:5px 0 2px; padding:2px 6px;
        background:#e4e4e7; color:#000; border:1px solid #000;
      }

      /* ============================================================
         Shared table base — tight rows, hairline black borders. =====
         ============================================================ */
      .zs-eob, .zs-wr, .zs-crew, .zs-wtd, .zs-chk { width:100%; border-collapse:collapse; }
      .zs-eob th, .zs-eob td,
      .zs-wr  th, .zs-wr  td,
      .zs-crew th, .zs-crew td,
      .zs-wtd th, .zs-wtd td,
      .zs-chk th, .zs-chk td {
        border:1px solid #000; padding:1px 4px; font-size:8.5px;
        vertical-align:middle; line-height:1.1;
      }

      /* Eyes on the Biz — same grayscale grid */
      .zs-eob th { background:#e4e4e7; font-size:7.5px; font-weight:800; letter-spacing:.03em; text-transform:uppercase; text-align:center; font-family:'Five Below Gothic', Arial, sans-serif; }
      .zs-eob .zs-grp { background:#a1a1aa; color:#000; font-size:9px; }
      .zs-eob .zs-sub { background:#e4e4e7; font-size:7.5px; font-weight:800; text-align:center; text-transform:uppercase; }
      .zs-eob .zs-lbl { background:#f4f4f5; font-weight:700; text-align:center; font-size:7.5px; padding:1px 3px; text-transform:uppercase; letter-spacing:.02em; }
      .zs-eob .zs-fb  { text-align:center; font-weight:700; }
      .zs-eob .zs-narrow { min-width:24px; width:24px; font-size:7px; }
      .zs-eob-row { background:#f4f4f5 !important; font-weight:900; text-align:center; }
      .zs-eob td { height:15px; text-align:center; min-width:40px; }
      .zs-eob-split { display:grid; grid-template-columns: 4fr 7fr; gap:4px; align-items:stretch; }
      .zs-eob-split > table { height:100%; }
      .zs-eob-single { /* Single-column Today table after WTD financials were
                          relocated to Manager Dashboard 2026-06-12. Caps
                          width so the table doesn't stretch the page edge
                          to edge — keeps the in-store form's compact feel. */
                       max-width: 60%; }

      /* World Recovery / Store Focus / QR row */
      .zs-row3 { display:grid; grid-template-columns: 2fr 1fr 1fr; gap:4px; margin:4px 0; }
      .zs-box { border:1px solid #000; }
      .zs-box-title { background:#a1a1aa; color:#000; padding:1px 6px; font-size:9px; font-weight:900; letter-spacing:.03em; text-transform:uppercase; font-family:'Five Below Gothic', Arial, sans-serif; border-bottom:1px solid #000; }
      .zs-focus-lbl { padding:3px 6px; font-size:8.5px; border-bottom:1px solid #000; }
      .zs-focus-area {
        padding: 4px;
        min-height: 120px;
        max-height: 140px;
        overflow: hidden;
        font-size: 8.5px;
        line-height: 1.15;
        /* Box is the ORIGINAL zone-sheet Store-Focus size (same height
           as the World Recovery box beside it, matching the physical
           form). We DON'T resize the box to fit text — the JS auto-fit
           (focusRef in ZoneSheetPrintPage) sizes the TEXT to the box,
           shrinking the font via a hidden ruler locked to the printed
           column width so the whole message fits instead of clipping
           (user: "it's cutting it off" / "all the boxes need to stay
           the same size... size the text to the box"). The -webkit-box
           clamp is only a pre-JS fallback. */
        white-space: pre-wrap;
        word-break: break-word;
        display: -webkit-box;
        -webkit-line-clamp: 10;
        -webkit-box-orient: vertical;
      }
      .zs-wr th { background:#e4e4e7; font-size:7.5px; font-weight:800; font-family:'Five Below Gothic', Arial, sans-serif; }
      .zs-wr td { height:15px; font-size:8.5px; }
      .zs-wr-lbl { background:#f4f4f5; font-weight:900; text-align:center; }
      /* World Recovery box: let the task table fill the box height so its
         rows stretch to match the taller Store Focus / QR boxes beside it,
         instead of floating at the top with empty space (user 2026-06:
         "the table doesn't fit correctly"). */
      .zs-wr-box { display:flex; flex-direction:column; }
      .zs-wr-box .zs-wr { flex:1 1 auto; height:100%; }
      .zs-wr-box .zs-wr td { height:auto; }
      .zs-qr-box { display:flex; flex-direction:column; }
      .zs-qr { padding:3px; display:flex; justify-content:center; align-items:center; flex:1; }

      /* Win the Day — uniform 4-column business grid.
         2026-08-30 (v7). The label content lives inside a DIV
         wrapper (.zs-wtd-lbl class) inside the td. This matters
         because browsers ignore max-height on td — table
         layout auto-sizes cells to their tallest content. Putting
         the max-height + overflow:hidden on an inner DIV lets us
         CLIP the label to exactly 2 lines and stop the row from
         growing to 3 lines when a long default label wraps. */
      .zs-wtd {
        table-layout: fixed;
        width: 100%;
      }
      .zs-wtd col.zs-wtd-lbl-col { width: 17%; }
      .zs-wtd col.zs-wtd-cmpl-col { width: 8%; }
      .zs-wtd th { background:#e4e4e7; font-size:7.5px; font-weight:800; font-family:'Five Below Gothic', Arial, sans-serif; }
      .zs-wtd-h { text-align:center; }
      .zs-wtd td {
        font-size: 8px;
        vertical-align: middle;
        padding: 3px 5px;
      }
      .zs-wtd-lbl-cell {
        overflow: hidden;
      }
      .zs-wtd-lbl {
        text-align: left;
        line-height: 1.2;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        word-break: break-word;
        max-height: 20px;
      }
      .zs-wtd .zs-cmpl {
        white-space: nowrap;
        overflow: hidden;
      }
      .zs-wtd-empty {
        border: 0 !important;
        background: transparent !important;
      }

      /* Completion cell — black text (printable). Removed the green
         shade that didn't exist on the original form. */
      .zs-cmpl {
        text-align:center; font-weight:800; min-width:50px;
        text-transform:uppercase; font-family:'Five Below Gothic', Arial, sans-serif;
        font-size:8.5px; color:#000;
      }

      /* Crew table */
      .zs-crew th { background:#e4e4e7; font-size:7.5px; font-weight:800; text-align:center; font-family:'Five Below Gothic', Arial, sans-serif; }
      .zs-crew td { height:15px; font-size:8px; text-align:center; }
      /* Coverage/Zone/Tasks column stays left-aligned so long
         coverage text reads naturally. NAME column is centered
         (2026-08-30) so the WowCrew members are the visual hero
         of the sheet — "we're excited to see them" (user). */
      .zs-crew td:nth-child(4) { text-align:left; }
      .zs-crew-name { font-weight: 900; letter-spacing: 0.02em; }
      .zs-crew-example { color:#52525b; font-style:italic; }
      .zs-crew-example td { background:#fafafa; }

      /* Manager checklist */
      .zs-chk th { background:#e4e4e7; font-size:7.5px; font-weight:800; text-align:left; padding:1px 6px; font-family:'Five Below Gothic', Arial, sans-serif; }
      .zs-chk th:nth-child(2) { text-align:center; width:100px; }
      .zs-chk td { font-size:8.5px; padding:1px 5px; }
      .zs-chk-grp td { background:#a1a1aa; color:#000; font-weight:900; text-transform:uppercase; letter-spacing:.03em; font-size:8.5px; padding:2px 6px; font-family:'Five Below Gothic', Arial, sans-serif; }
      .zs-chk-sub { font-size:7.5px; color:#52525b; font-style:italic; }

      /* Highlight notes between Opening & Closing sections */
      .zs-note { font-size:8.5px; font-weight:900; text-transform:uppercase; padding:3px 6px; border:1px solid #000; background:#fafafa; margin:3px 0; line-height:1.2; }

      /* Bottom NOTES block */
      .zs-notes-block { border:1px solid #000; margin-top:5px; }
      .zs-notes-lbl { background:#a1a1aa; color:#000; padding:1px 6px; font-size:9px; font-weight:900; font-family:'Five Below Gothic', Arial, sans-serif; border-bottom:1px solid #000; }
      .zs-notes-fill { padding:4px 6px; min-height:42px; font-size:8.5px; white-space:pre-wrap; }

      /* ============================================================
         Print media — strict 2-page Letter output with hard DOM
         isolation. The print overlay is portal-mounted directly to
         document.body (see ZoneSheetPage.js), so we can hide every
         OTHER body child during print with one rule. This kills the
         old "app UI bleeds onto the printed PDF" bug (Manager
         Dashboard / sticky headers / nav strips / toasts /
         hamburger menus all stripped out for print). The portal'd
         overlay itself is dropped from fixed to static so its content
         paginates naturally to ~2 pages instead of being constrained
         to a single viewport. No zoom hacks — the in-store form
         sizes (~8.5px base) are kept verbatim; if the output ever
         comes up short, fix the layout, not with a global zoom.
         (User-approved print isolation plan 2026-06-18.) ============ */
      @media print {
        @page { size: Letter; margin: 0.3in 0.35in; }

        /* ROOT-CAUSE FIX (2026-06) — Page 2 (Manager Checklist) bled
           onto Page 1 even with forced break-before/break-after set.
           Cause: Radix dialogs use react-remove-scroll, which locks
           <body> with "overflow:hidden !important" via a
           [data-scroll-locked] rule whenever a dialog has been opened
           (Today's Targets / Daily Briefing). That clipping ancestor
           makes Chrome collapse the ENTIRE print job into a single
           page and silently DROP the forced page break — so the
           checklist flowed right under the Zone Sheet on sheet 1.
           Neutralize the clip for print. The body selector uses
           :has() so its specificity (0,1,1) plus later source order
           beats [data-scroll-locked] (0,1,1); html is forced too so
           neither element can clip the paginated flow. Verified via
           headless print-to-PDF: 2 pages, checklist starts on Page 2. */
        html { overflow: visible !important; height: auto !important; }
        body:has([data-testid="zone-sheet-print-overlay"]) {
          overflow: visible !important;
          height: auto !important;
        }

        /* DOM isolation — hide everything that isn't the print
           overlay. Selector targets DIRECT body children so any
           descendant of the overlay (the .zs container, tables,
           etc.) stays visible. Wrapped in body:has(...) so the
           rule ONLY activates when the portal'd overlay is mounted
           (the embed-from-Zone-Sheet flow). For the standalone URL
           flow (route=zone-sheet-print), the print page renders
           directly inside #root without the overlay wrapper —
           :has() evaluates false, and nothing gets hidden. :has() is
           supported in Chrome 105+, Safari 15.4+, Firefox 121+ (all
           browsers we target for the in-store iPads + manager
           phones). */
        body:has([data-testid="zone-sheet-print-overlay"]) > *:not([data-testid="zone-sheet-print-overlay"]) {
          display: none !important;
        }
        body > [data-testid="zone-sheet-print-overlay"] {
          display: block !important;
          position: static !important;
          inset: auto !important;
          top: auto !important;
          left: auto !important;
          right: auto !important;
          bottom: auto !important;
          width: auto !important;
          height: auto !important;
          max-width: none !important;
          max-height: none !important;
          overflow: visible !important;
          z-index: auto !important;
          background: #fff !important;
        }

        .zs-toolbar { display:none !important; }
        .zs { padding:0; max-width:none; }
        .zs-page {
          width:100%;
          page-break-after:always;
          break-after:page;
        }
        .zs-page:last-child { page-break-after:auto; break-after:auto; }
        /* Force Page 2 (Manager Checklist) onto its own sheet. Dropping
           page-break-inside:avoid above (it made Chrome ignore the forced
           break-after when Page 1 content sat right at the Letter boundary,
           bleeding the checklist header onto Page 1 — user report 2026-06)
           plus this explicit break-before guarantees a clean 2-page split.
           Chrome collapses the adjacent break-after + break-before into one
           break, so no blank page appears between them. */
        .zs-page-2 { page-break-before: always; break-before: page; }
        body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }

        /* ============================================================
           Page 1 (Daily Zone Sheet) print upscale. Page 2 (Manager
           Checklist) already fills the Letter sheet at the base 8.5px
           cell sizes — it's a 20+ row checklist. Page 1 is much
           sparser (3 checkpoint rows + small KPI grid + 4-col Win The
           Day + crew table padded to 6 rows) and at base sizes only
           fills ~56% of a Letter page (44% wasted whitespace). We
           scale Page 1 UP via targeted px overrides — NOT zoom,
           which previously caused phantom page-3 duplication when the
           browser's pagination disagreed with the zoom-distorted box
           heights. Font-size + explicit cell heights expand the
           layout-flow normally, so pagination stays at exactly 2
           pages. Approximate scale factor: ~1.55x cell heights,
           ~1.5x font sizes. (User ask 2026-06-18 "Page 1 only uses
           the upper portion of the letter page — fill it without the
           zoom hacks that duplicated pages.") =========================
           ============================================================ */
        /* Prevent the orphan-header bug: native browser default makes
           thead display: table-header-group, which causes the
           header row to RE-PRINT on every page the table spans. When
           Page 1 content is right at the page boundary, a single
           repeated header row can leak onto a near-empty Page 2 even
           though the table body all fits on Page 1. Forcing
           table-row-group removes the repetition. We also keep
           every table + its rows from splitting across a page break
           so no table is half-printed. (User report 2026-06-18 —
           printed PDF had a stray "NAME | SHIFT | BREAK | …" header
           row alone on page 2.) */
        .zs thead { display: table-row-group; }
        .zs table { page-break-inside: avoid; break-inside: avoid; }
        .zs tr { page-break-inside: avoid; break-inside: avoid; }

        .zs-page-1 { font-size:12px; }
        .zs-page-1 .zs-title h1 { font-size:28px; }
        .zs-page-1 .zs-date,
        .zs-page-1 .zs-fill { font-size:11px; }
        .zs-page-1 .zs-howto { font-size:9px; }
        .zs-page-1 .zs-logo { height:50px; }
        .zs-page-1 .zs-qr-small svg { width:60px; height:60px; }

        .zs-page-1 .zs-h2,
        .zs-page-1 .zs-h2-sub {
          font-size:13px; padding:3px 8px; margin:5px 0 3px;
        }

        .zs-page-1 .zs-eob th,
        .zs-page-1 .zs-wr th,
        .zs-page-1 .zs-crew th,
        .zs-page-1 .zs-wtd th { font-size:10px; padding:2px 5px; }
        .zs-page-1 .zs-eob td,
        .zs-page-1 .zs-wr td,
        .zs-page-1 .zs-crew td {
          font-size:11px; height:22px; padding:2px 5px;
        }
        /* Win The Day — dense uniform grid on print.
           v7: label DIV clips to exactly 2 lines via max-height
           (font-size 9px x line-height 1.2 = 10.8px per line, so
           22px height fits 2 lines exactly with hard-clip on the
           3rd). Every WTD row is then the same height because no
           label cell can grow past 2 lines of text.
           v8 (2026-08-30, user: "stretched out the wow crew table
           too much when it should've been the win the day section"):
           bump td height to 50px so WTD becomes the dominant
           visual block on Page 1. Height on td is a MIN — the
           label DIV stays clamped at 2 lines, cell just adds
           breathing room around the text. */
        .zs-page-1 .zs-wtd td {
          font-size: 9px;
          height: 50px;
          padding: 4px 6px;
        }
        .zs-page-1 .zs-wtd th { font-size: 9px; padding: 3px 5px; }
        .zs-page-1 .zs-wtd-lbl {
          -webkit-line-clamp: 2;
          line-height: 1.2;
          max-height: 22px;
          font-size: 9px;
        }
        .zs-page-1 .zs-wtd .zs-cmpl {
          font-size: 9px;
          min-width: 0;
          padding: 4px 3px;
          white-space: nowrap;
        }
        .zs-page-1 .zs-eob .zs-grp { font-size:12px; }
        .zs-page-1 .zs-eob .zs-sub { font-size:10px; }
        .zs-page-1 .zs-eob .zs-lbl { font-size:10px; padding:2px 4px; }
        .zs-page-1 .zs-eob .zs-narrow { font-size:9px; min-width:30px; width:30px; }
        .zs-page-1 .zs-eob-row { font-size:12px; }
        .zs-page-1 .zs-cmpl { font-size:11px; min-width:62px; }

        .zs-page-1 .zs-box-title,
        .zs-page-1 .zs-notes-lbl {
          font-size:11px; padding:3px 6px;
        }
        .zs-page-1 .zs-focus-lbl { padding:4px 6px; font-size:11px; }
        .zs-page-1 .zs-focus-area {
          /* 2026-08-30 — Sized to fit the manager's default focus
             (~200 chars = 8 lines at 11px in a ~240px-wide box) so
             the whole message prints without clipping. Line-clamp
             at 10 gives a small safety net if a manager types a
             novel-length focus. User 2026-08-30: "the text box is
             cut off". */
          padding: 5px;
          min-height: 120px;
          max-height: 140px;
          font-size: 11px;
          line-height: 1.25;
          -webkit-line-clamp: 10;
        }
        /* WowCrew row height reverted to original 22px (user
           2026-08-30: "stretched out the wow crew table too much").
           WTD takes the extra vertical space instead — see
           .zs-page-1 .zs-wtd td above. */
        .zs-page-1 .zs-crew td { height: 22px; }
        .zs-page-1 .zs-wr td { height: 22px; }
        .zs-page-1 .zs-wr-lbl { font-size:11px; }
        .zs-page-1 .zs-qr svg { width:94px; height:94px; }
        .zs-page-1 .zs-crew-example td { font-size:10px; }

        /* Page 2 (Daily Manager Checklist) — geometry-only adjustments.
           DO NOT touch font sizes / scaling / zoom here. We bump cell
           padding on the checklist rows + group headers and restore
           the breathing room around the OPENING / CLOSING h2 headers
           so the task tables grow back to the original ~88% page
           fill. NOTES box stays at its native ~42px height (the
           previous min-height: 2.1in was the regression that
           bloated NOTES to ~19% of the page; removed). User-approved
           fix 2026-06-19 to restore Page 2 proportions to the
           original PDF without zoom/scaling/font changes. */
        .zs-page-2 .zs-chk td { padding: 2px 5px; }
        .zs-page-2 .zs-chk-grp td { padding: 3px 6px; }
        .zs-page-2 .zs-h2-sub { margin: 6px 0 3px; }
        .zs-page-2 .zs-note { padding: 4px 6px; margin: 4px 0; }
        .zs-page-2 .zs-notes-fill { min-height: 1in; }
      }
    `}</style>
  );
}
